export const IDENTITY_PROVIDERS = {
  Local: 1,
  Google: 2,
  Facebook: 3,
  LinkedIn: 4,
} as const;

export const LINKEDIN_STORAGE = {
  state: 'linkedin_oauth_state',
  redirectUri: 'linkedin_oauth_redirect_uri',
  pendingProfile: 'linkedin_pending_profile',
  session: 'linkedin_session',
  accessToken: 'linkedin_access_token',
} as const;

export const GOOGLE_STORAGE = {
  state: 'google_oauth_state',
  redirectUri: 'google_oauth_redirect_uri',
  pendingProfile: 'google_pending_profile',
  session: 'google_session',
  accessToken: 'google_access_token',
} as const;

export interface ExternalPendingProfile {
  email: string;
  givenName?: string;
  mobile?: string;
  pictureUrl?: string;
  identityProviderId: number;
}

export type LinkedInPendingProfile = ExternalPendingProfile;
export type GooglePendingProfile = ExternalPendingProfile;

function readPending(key: string): ExternalPendingProfile | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as ExternalPendingProfile;
  } catch {
    return null;
  }
}

export function readLinkedInPendingProfile(): LinkedInPendingProfile | null {
  return readPending(LINKEDIN_STORAGE.pendingProfile);
}

export function clearLinkedInPendingProfile(): void {
  sessionStorage.removeItem(LINKEDIN_STORAGE.pendingProfile);
}

export function markLinkedInSession(accessToken?: string | null): void {
  localStorage.setItem(LINKEDIN_STORAGE.session, '1');
  if (accessToken) {
    sessionStorage.setItem(LINKEDIN_STORAGE.accessToken, accessToken);
  }
}

export function clearLinkedInSession(): void {
  localStorage.removeItem(LINKEDIN_STORAGE.session);
  sessionStorage.removeItem(LINKEDIN_STORAGE.accessToken);
}

export function isLinkedInSession(identityProviderId?: number): boolean {
  return identityProviderId === IDENTITY_PROVIDERS.LinkedIn
    || localStorage.getItem(LINKEDIN_STORAGE.session) === '1';
}

export function readGooglePendingProfile(): GooglePendingProfile | null {
  return readPending(GOOGLE_STORAGE.pendingProfile);
}

export function clearGooglePendingProfile(): void {
  sessionStorage.removeItem(GOOGLE_STORAGE.pendingProfile);
}

export function markGoogleSession(accessToken?: string | null): void {
  localStorage.setItem(GOOGLE_STORAGE.session, '1');
  if (accessToken) {
    sessionStorage.setItem(GOOGLE_STORAGE.accessToken, accessToken);
  }
}

export function clearGoogleSession(): void {
  localStorage.removeItem(GOOGLE_STORAGE.session);
  sessionStorage.removeItem(GOOGLE_STORAGE.accessToken);
}

export function isGoogleSession(identityProviderId?: number): boolean {
  return identityProviderId === IDENTITY_PROVIDERS.Google
    || localStorage.getItem(GOOGLE_STORAGE.session) === '1';
}

export function readExternalPendingProfile(): ExternalPendingProfile | null {
  return readGooglePendingProfile() ?? readLinkedInPendingProfile();
}

export function writeExternalPendingProfile(pending: ExternalPendingProfile): void {
  const key = pending.identityProviderId === IDENTITY_PROVIDERS.Google
    ? GOOGLE_STORAGE.pendingProfile
    : LINKEDIN_STORAGE.pendingProfile;
  const otherKey = pending.identityProviderId === IDENTITY_PROVIDERS.Google
    ? LINKEDIN_STORAGE.pendingProfile
    : GOOGLE_STORAGE.pendingProfile;
  sessionStorage.setItem(key, JSON.stringify(pending));
  sessionStorage.removeItem(otherKey);
}

export function clearExternalPendingProfile(): void {
  clearLinkedInPendingProfile();
  clearGooglePendingProfile();
}
